import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { FiEye, FiUsers, FiSearch, FiBell } from "react-icons/fi";
import { useStickyState } from "../../hooks/useStickyState";
import SendNotificationModal from "./SendNotificationModal";
import { useScrollRestoration } from "../../hooks/useScrollRestoration";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const avatarColors = [
  "bg-blue-100 text-blue-600",
  "bg-purple-100 text-purple-600",
  "bg-green-100 text-green-600",
  "bg-orange-100 text-orange-600",
  "bg-rose-100 text-rose-600",
  "bg-teal-100 text-teal-600",
];

const getAvatarColor = (name = "") => avatarColors[name.charCodeAt(0) % avatarColors.length];

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useStickyState("", "ul_search");
  const [debouncedSearch, setDebouncedSearch] = useStickyState("", "ul_debouncedSearch");
  const [filter] = useState("All");
  const [currentPage, setCurrentPage] = useStickyState(1, "ul_currentPage");
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const debounceTimer = useRef(null);
  const [notificationModalUserId, setNotificationModalUserId] = useState(null);

  useScrollRestoration("user_list", !loading && users.length > 0);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), 500);
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filter, currentPage]);

  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${baseUrl}/users/list?search=${debouncedSearch}&status=${filter}&page=${currentPage}&perPage=${itemsPerPage}`,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const result = await response.json();
      if (result.status === true && Array.isArray(result.data)) {
        setUsers(result.data);
        setTotalUsers(result.pagination?.total || 0);
      } else {
        throw new Error(result?.message || "Failed to fetch user list");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  if (endPage - startPage < maxVisiblePages - 1) startPage = Math.max(1, endPage - maxVisiblePages + 1);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <FiUsers size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Users</h2>
                <p className="text-sm text-gray-400 mt-0.5">Manage all registered users</p>
              </div>
            </div>
            <div className="bg-white border border-gray-100 shadow-sm rounded-xl px-4 py-2.5 text-center hidden sm:block">
              <p className="text-xs text-gray-400">Total Users</p>
              <p className="text-xl font-bold text-gray-800">{totalUsers.toLocaleString()}</p>
            </div>
          </div>

          {/* Search bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="relative w-full sm:w-80">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="text"
                placeholder="Search by username or email..."
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex justify-center items-center py-24">
              <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">
              {error}
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Table header row */}
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">All Users</p>
                  <p className="text-xs text-gray-400">
                    {users.length > 0
                      ? `Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalUsers)} of ${totalUsers}`
                      : "No results"}
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                        <th className="px-5 py-3 w-10">#</th>
                        <th className="px-5 py-3">User</th>
                        <th className="px-5 py-3 hidden md:table-cell">Email</th>
                        <th className="px-5 py-3 hidden md:table-cell">Country</th>
                        <th className="px-5 py-3 text-right hidden sm:table-cell">Coins</th>
                        <th className="px-5 py-3 text-center hidden lg:table-cell">Mining</th>
                        <th className="px-5 py-3 text-center hidden lg:table-cell">Strike</th>
                        <th className="px-5 py-3 hidden xl:table-cell">Referral</th>
                        <th className="px-5 py-3 hidden xl:table-cell">Joined</th>
                        <th className="px-5 py-3 text-center w-16">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="py-16 text-center">
                            <div className="flex flex-col items-center gap-2 text-gray-400">
                              <FiUsers size={32} className="text-gray-300" />
                              <p className="text-sm">No users found</p>
                              {search && <p className="text-xs">Try a different search term</p>}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        users.map((user, index) => {
                          const initials = (user.username || "U")[0].toUpperCase();
                          const avatarCls = getAvatarColor(user.username || "");
                          return (
                             <tr className="hover:bg-gray-50 transition">
                              <td className="px-5 py-3.5 text-gray-400 text-xs">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td className="px-5 py-3.5">
                                <button
                                  onClick={() => navigate(`/user-full-details/${user.userId}`, { state: { from: '/user' } })}
                                  className="flex items-center gap-3 text-left group"
                                  title="View user details"
                                >
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                                    {initials}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-800 group-hover:text-blue-600 group-hover:underline transition truncate max-w-[130px]">
                                      {user.username || "—"}
                                    </p>
                                    <p className="text-xs text-gray-400 md:hidden truncate max-w-[130px]">
                                      {user.email}
                                    </p>
                                  </div>
                                </button>
                              </td>
                              <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">
                                <span className="block truncate max-w-[200px]">{user.email}</span>
                              </td>
                              <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">
                                {user.country || "—"}
                              </td>
                              <td className="px-5 py-3.5 text-right font-semibold text-gray-700 hidden sm:table-cell whitespace-nowrap">
                                {parseFloat(user.coin || 0).toFixed(2)}
                              </td>
                              <td className="px-5 py-3.5 text-center hidden lg:table-cell">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  user.isMiningActive ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"
                                }`}>
                                  {user.isMiningActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-center hidden lg:table-cell">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  user.strike > 0 ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-500"
                                }`}>
                                  {user.strike ?? 0}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 font-mono text-xs text-gray-400 hidden xl:table-cell">
                                {user.referralCode || "—"}
                              </td>
                              <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap text-xs hidden xl:table-cell">
                                {formatDate(user.createdAt)}
                              </td>
                              <td className="px-5 py-3.5">
                                <div className="flex items-center justify-center gap-1.5">
                                  <button
                                    onClick={() => navigate(`/user-full-details/${user.userId}`)}
                                    className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition"
                                    title="View Details"
                                  >
                                    <FiEye size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setNotificationModalUserId(user.userId);
                                    }}
                                    className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-50 transition"
                                    title="Send Notification"
                                  >
                                    <FiBell size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 items-center">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &laquo;
                  </button>
                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => setCurrentPage(pg)}
                      className={`px-4 py-2 border rounded-lg text-sm transition ${
                        currentPage === pg ? "bg-blue-500 text-white border-blue-500" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      {pg}
                    </button>
                  ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <SendNotificationModal
        isOpen={!!notificationModalUserId}
        onClose={() => setNotificationModalUserId(null)}
        userId={notificationModalUserId}
      />
    </div>
  );
};

export default UserList;
